import React, { useEffect, useRef } from "react";
import ReadOnlyInput from "./ReadOnlyInput";
import InputWrapper from "./InputWrapper";
import { InputAdornment, TextField } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { Controller } from "react-hook-form";
import { DEFAULT_SIZE } from "../../../utils/constant";
import PropTypes from "prop-types";

function formatSSN(value) {
  const digits = value.replace(/\D/g, "");
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 5);
  const part3 = digits.slice(5, 9);

  let formatted = part1;
  if (part2) formatted += `-${part2}`;
  if (part3) formatted += `-${part3}`;

  return formatted;
};

export default function ProSSNField({
                                      name,
                                      mode,
                                      label,
                                      rules,
                                      control,
                                      isAddMode,
                                      fieldProps = {},
                                      size = DEFAULT_SIZE,
                                      startAdornment = null,
                                      onCustomBlur,
                                      isLast4,
                                      ...rest
                                    }) {
  const inputRef = useRef(null);
  const maskPrefix = "XXX-XX-";
  const prefixLength = maskPrefix.length;
  const { canView = false, canAdd = false, canEdit = false } = rest.permission || {};
  if (!canView || (isAddMode && !canAdd)) return null;
  const editModeAllowed = isAddMode ? canAdd : canEdit;
  if (mode === "read" || (mode === "edit" && !editModeAllowed)) return <ReadOnlyInput name={name} label={label} size={size} type={isLast4 ? "ssn" : ""} {...rest} rules={rules} />;
  return (
    <InputWrapper size={size}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
          const digits = (value ?? "").replace(/\D/g, "").slice(-4);
          const displayValue = `XXX-XX-${digits}`;

          const ensureCursorAfterPrefix = e => {
            if (e?.target?.selectionStart < prefixLength) {
              setTimeout(() =>
                e.target.setSelectionRange(prefixLength, e.target.value.length),
              0);
            }
          };

          useEffect(() => {
            if (inputRef.current && inputRef.current.selectionStart < 7) {
              inputRef.current.setSelectionRange(7, 7);
            }
          }, [displayValue]);

          return (
            <TextField
              inputRef={inputRef}
              fullWidth
              variant="outlined"
              value={displayValue}
              onChange={e => {
                const inputDigits = e.target.value.replace(/\D/g, "");
                const last4 = inputDigits.slice(-4);
                onChange(last4); 
              }}
              onFocus={ensureCursorAfterPrefix}
              onClick={ensureCursorAfterPrefix}
              onKeyDown={e => {
                const { selectionStart, value } = e.target;
                
                if (selectionStart <= prefixLength && e.key === "Backspace") {
                  e.preventDefault();
                  return;
                }
                if (
                  e.key === "Delete" && selectionStart === prefixLength && value.length > prefixLength
                ) {
                  return;
                }
                if (selectionStart < prefixLength && e.key === "Delete") {e.preventDefault();}
              }}
              label={
                <span>
                  {label}
                  {rules?.required && <span style={{ color: "red" }}> *</span>}
                </span>
              }
              placeholder="XXX-XX-####"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                inputProps: { maxLength: 11 },
              }}
              InputLabelProps={{ shrink: true }}
              helperText={error?.message || null}
              error={!!error}
              {...fieldProps}
            />
          );
        }}
      />
    </InputWrapper>
  );
}

ProSSNField.propTypes = {
  name: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["read", "edit"]).isRequired,
  label: PropTypes.string.isRequired,
  rules: PropTypes.object.isRequired,
  startAdornment: PropTypes.node,
  control: PropTypes.object.isRequired,
  isAddMode: PropTypes.bool,
  fieldProps: PropTypes.object,
  permission: PropTypes.shape({
    canView: PropTypes.bool,
    canAdd: PropTypes.bool,
    canEdit: PropTypes.bool
  }),
  formValues: PropTypes.object,
  size: PropTypes.string,
  handleReadValueClick: PropTypes.func
};